import { gql, useQuery } from '@apollo/client'
import _ from 'lodash'
import { useMemo } from 'react'

import { useDateFilter } from '../../../dateFilter'
import { useGroupFilter } from '../../../groupFilter'
import { corporateGroupTableConfigs, useVariables } from '../../../variables'
import { IApiDataType } from '../../types'

interface IDataType {
  weekdayTrendLocationMetricValues: {
    nodes: {
      locationId: number
      startDate: string
      endDate: string
      businessDayOfWeek: number
      metricData: Record<
        string,
        {
          name: string
          unit: string
          value: number
        }
      >
    }[]
  }
}

// Define the type for processed data
interface IProcessedDataItem {
  locationId: number
  [key: string]: number // Dynamic keys for all metrics
}

const query = gql`
  query GetWeekdayTrendLocationMetricData(
    $iStartDate: Date!
    $iEndDate: Date!
    $iLocationGroupIds: [Int!]!
    $iMetricGroups: [String!]!
  ) {
    weekdayTrendLocationMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: {
        location_group_ids: $iLocationGroupIds
        metric_groups: $iMetricGroups
      }
    ) {
      nodes {
        locationId
        startDate
        endDate
        businessDayOfWeek
        metricData
      }
    }
  }
`

const METRIC_GROUPS = [
  'Late Night - Hourly Labor Sales',
  'Late Night - Hourly Labor Cost',
  'Late Night - Hourly Labor Minutes',
]

const LABOR_METRICS = [
  'hourly_labor_sales',
  'hourly_labor_cost',
  'hourly_labor_minutes',
] as const

export const ghaiPopWeekdayAvgLateNightConfigs = {
  location: 'link', // for sorting
  '<%- JSON(corporateGroup?.tableColumns.slice(0, 1)) %>':
    corporateGroupTableConfigs['<%- JSON(corporateGroup?.tableColumns) %>'],
  '<%- JSON(ghaiPopLateNightTableColumns.price) %>': 'price',
  '<%- JSON(ghaiPopLateNightTableColumns.percent) %>': 'percent',
  '<%- JSON(ghaiPopLateNightTableColumns.hours) %>': 'hours',
} as const

const useGhaiPopWeekdayAvgLateNight = () => {
  const groupFilterData = useGroupFilter()
  const dateFilterData = useDateFilter()
  const { variables } = useVariables()
  const locationList = groupFilterData.groupFilter?.list || []
  const laborMetric = variables?.ghaiPopLateNightMetricOptions
    ?.value?.[0]?.[0] as typeof LABOR_METRICS[number] | undefined

  const { startDate, endDate } = useMemo(
    () => ({
      startDate: dateFilterData.startDate,
      endDate: dateFilterData.endDate,
    }),
    [dateFilterData],
  )
  const { data, loading } = useQuery<IDataType>(query, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iLocationGroupIds: groupFilterData.groupFilter?.ids,
      iMetricGroups: METRIC_GROUPS,
    },
    skip: !startDate || !endDate || !groupFilterData.groupFilter?.ids,
  })

  return {
    data: useMemo((): IApiDataType => {
      const metricData = data?.weekdayTrendLocationMetricValues.nodes

      if (!metricData || !laborMetric) return null

      // Group data by locationId, businessDayOfWeek, and hour
      const groupedData: Record<
        string,
        { total: Record<string, number>; count: Record<string, number> }
      > = {}

      metricData.forEach((record) => {
        const { locationId, businessDayOfWeek, metricData: metrics } = record
        const weekday = businessDayOfWeek % 7 // Ensure 0-6 range (0 = Sunday)

        const locationKey = `${locationId}`
        if (!groupedData[locationKey]) {
          groupedData[locationKey] = {
            total: {},
            count: {},
          }
        }

        // Process each hour (21, 22, 23, 24, 25, 26)
        for (let hour = 21; hour <= 26; hour++) {
          // Process each metric type (sales, cost, percent)
          LABOR_METRICS.forEach((metricType) => {
            const metricKey = `late_night_${hour}_${metricType}`
            const dataKey = `weekday_${weekday}_${metricKey}`

            if (!groupedData[locationKey].total[dataKey]) {
              groupedData[locationKey].total[dataKey] = 0
              groupedData[locationKey].count[dataKey] = 0
            }

            // Only process if the metric exists in the data
            if (
              metrics[metricKey] &&
              ['DOLLAR', 'MINUTE'].includes(metrics[metricKey].unit)
            ) {
              groupedData[locationKey].total[dataKey] +=
                metrics[metricKey].value
              groupedData[locationKey].count[dataKey] += 1
            }
          })
        }
      })

      // Calculate averages and create final data structure
      const processedData: IProcessedDataItem[] = Object.keys(groupedData).map(
        (locationKey) => {
          const locationId = parseInt(locationKey)
          const result: IProcessedDataItem = { locationId }

          // For each weekday (0-6)
          for (let weekday = 0; weekday <= 6; weekday++) {
            // For each hour (21-26)
            for (let hour = 21; hour <= 26; hour++) {
              // Calculate averages for each metric
              LABOR_METRICS.forEach((metricType) => {
                const metricKey = `late_night_${hour}_${metricType}`
                const dataKey = `weekday_${weekday}_${metricKey}`

                const total = groupedData[locationKey].total[dataKey] || 0
                const count = groupedData[locationKey].count[dataKey] || 0

                if (
                  metricType === 'hourly_labor_sales' ||
                  metricType === 'hourly_labor_cost' ||
                  metricType === 'hourly_labor_minutes'
                ) {
                  // Calculate average for dollar values
                  result[dataKey] = count > 0 ? total / count : 0
                }
              })

              // Calculate labor percent from sales and cost
              const salesKey = `weekday_${weekday}_late_night_${hour}_hourly_labor_sales`
              const costKey = `weekday_${weekday}_late_night_${hour}_hourly_labor_cost`
              const percentKey = `weekday_${weekday}_late_night_${hour}_hourly_labor_percent`

              const sales = result[salesKey] || 0
              const cost = result[costKey] || 0

              // Calculate labor percent as cost/sales
              const laborPercent = (() => {
                if (sales > 0) return (cost / sales) * 100
                if (cost > 0) return 100
                return 0
              })()
              result[percentKey] = laborPercent
            }
          }

          return result
        },
      )

      // Transform the processed data into the required API format
      const tableData = processedData.map((item) => {
        const { locationId, ...rest } = item
        const currentlocatonInfo = locationList.find(
          (location) => location.id === locationId,
        )
        const camelCasedRest = Object.keys(rest).reduce((acc, key) => {
          acc[_.camelCase(key)] = rest[key]
          return acc
        }, {} as Record<string, number>)

        return {
          parentId: 'root',
          id: locationId.toString(),
          groupInfo: currentlocatonInfo?.groupBy || {},
          location: {
            id: locationId,
            link: currentlocatonInfo?.link || '',
            label: currentlocatonInfo?.name || '',
          },
          ...camelCasedRest,
        }
      })

      return tableData
    }, [data, laborMetric, locationList]),
    loading,
  }
}

export default useGhaiPopWeekdayAvgLateNight
